import { Portfolio, PortfolioWithExtraInfo } from '@/types';
import { InsightGetSymbolsPerTypeResponse } from '@orbis-systems/orbis-api-clients/insight';
import {
    AccountClassification,
    AccountStatus,
    Currency,
    formatQuote,
    Group,
    GroupWithExtraInfo,
    MarketCapClassification,
    Model,
    ModelWithExtraInfo,
    PositionType,
    SecurityType,
    TiprankOverviewWithExtraInfo,
} from '@orbis-systems/orbis-api-middleware';
import {
    Account,
    AccountWithExtraInfo,
    BranchInfo,
    GroupRtbs,
    Position,
    PositionWithExtraInfo,
    Rtbs,
} from '@orbis-systems/orbis-api-middleware/types';
import { divide, sumBy } from 'lodash';
import { hasBreakdownByAccounts } from './combinePositions';

export const combineAccountsWithRtbs = (
    accounts: Account[],
    branchRtbs: GroupRtbs[],
    fxRate: number = 1,
    baseCurrency: string = Currency.UsDollar,
): AccountWithExtraInfo[] => {
    const rtbsByAccountId = new Map((branchRtbs ?? []).map((item) => [item.accountId, item]));

    return accounts
        .filter((account) => account.status === AccountStatus.Active)
        .map((account) => {
            const {
                balance = 0,
                cashOnAccount = 0,
                markToMarket = 0,
                sodCashBalance = 0,
                sodMarginBalance = 0,
                sodShortBalance = 0,
                totalDividendAmount = 0,
                totalDividendYield = 0,
            } = rtbsByAccountId.get(account.accountId) || {};

            return {
                ...account,
                balance: balance * fxRate,
                cashOnAccount: cashOnAccount * fxRate,
                markToMarket: markToMarket * fxRate,
                sodCashBalance: sodCashBalance * fxRate,
                sodMarginBalance: sodMarginBalance * fxRate,
                sodShortBalance: sodShortBalance * fxRate,
                totalDividendAmount: totalDividendAmount * fxRate,
                totalDividendYield,
                baseCurrency,
            };
        });
};

export const combineModelsWithRtbs = (
    memberRealtimeBalances: GroupRtbs[],
    models: Model[],
    accountsWithRtbs: AccountWithExtraInfo[],
): ModelWithExtraInfo[] => {
    const rtbsByModelId = new Map(memberRealtimeBalances.map((item) => [item.modelId, item]));

    return models.map((model) => {
        const modelRtbsData = rtbsByModelId.get(model.id);

        const {
            balance = 0,
            cashOnAccount = 0,
            markToMarket = 0,
            sodCashBalance = 0,
            sodMarginBalance = 0,
            sodShortBalance = 0,
            totalDividendAmount = 0,
            totalDividendYield = 0,
        } = modelRtbsData || {};

        const accountIds = (model.accounts ?? []).map((account) => account.accountId);
        const accounts = accountsWithRtbs.filter((account) => accountIds.includes(account.accountId));

        (model.components ?? []).forEach((component) => {
            if (component.quote) {
                component.quote = formatQuote(component.quote);
            }
        });

        return {
            ...model,
            balance,
            cashOnAccount,
            markToMarket,
            sodCashBalance,
            sodMarginBalance,
            sodShortBalance,
            totalDividendAmount,
            totalDividendYield,
            accounts,
        };
    });
};

export const combineGroupsWithRtbs = (
    groups: Group[],
    accountsWithRtbs: AccountWithExtraInfo[],
): GroupWithExtraInfo[] => {
    return groups.map((group) => {
        const groupAccounts = accountsWithRtbs.filter((account) => group.accountIds.includes(account.accountId));

        const rtbs = groupAccounts.reduce<Rtbs>(
            (result, account) => {
                result.balance += account.balance;
                result.cashOnAccount += account.cashOnAccount;
                result.markToMarket += account.markToMarket;
                result.sodCashBalance += account.sodCashBalance;
                result.sodMarginBalance += account.sodMarginBalance;
                result.sodShortBalance += account.sodShortBalance;
                result.totalDividendAmount += account.totalDividendAmount;
                result.totalDividendYield += account.totalDividendYield;
                return result;
            },
            {
                balance: 0,
                cashOnAccount: 0,
                markToMarket: 0,
                sodCashBalance: 0,
                sodMarginBalance: 0,
                sodShortBalance: 0,
                totalDividendAmount: 0,
                totalDividendYield: 0,
            },
        );

        return {
            ...group,
            ...rtbs,
            accounts: groupAccounts,
        };
    });
};

interface CombinedBranchInfo extends BranchInfo {
    totalDividendAmount: number;
}

export const combineBranchInfo = (
    accountsWithRtbs: AccountWithExtraInfo[],
    modelsWithRtbs: ModelWithExtraInfo[],
    groupsWithRtbs: GroupWithExtraInfo[],
): CombinedBranchInfo => {
    return {
        balance: sumBy(accountsWithRtbs, (account) => account.balance),
        cash: sumBy(accountsWithRtbs, (account) => account.cashOnAccount),
        totalDividendAmount: sumBy(accountsWithRtbs, (account) => account.totalDividendAmount),
        monthlyCashflow: divide(
            sumBy(accountsWithRtbs, (account) => account.totalDividendAmount),
            12,
        ),
        portfolioValue: sumBy(accountsWithRtbs, (account) => account.markToMarket),
        totalAccounts: accountsWithRtbs.length,
        totalModels: modelsWithRtbs.length,
        totalGroups: groupsWithRtbs.length,
    };
};

/**
 * Formats and enriches portfolio positions with additional calculated metrics
 *
 * This function processes raw position data, filters by symbol and position type if specified,
 * and enriches each position with account information, weight calculations, and security type determination.
 *
 * @param positions - Raw position data from the API
 * @param accounts - Account information with extra details
 * @param inventoryAccounts - Inventory-specific account information
 * @param totalBalance - Total portfolio balance in USD
 * @param symbol - Optional filter by symbol
 * @param positionType - Optional filter by position type (long/short)
 * @param groupedSymbolsByFundType - Symbols grouped by fund type for accurate security type determination
 * @param fxRateBaseCurrencyToUSD - Exchange rate from base currency to USD for weight calculation
 * @returns Array of enriched positions with calculated metrics
 */
export function formatPortfolioPositions(
    positions: Position[],
    accounts: AccountWithExtraInfo[],
    inventoryAccounts: Account[],
    totalBalance: number,
    symbol?: string,
    positionType?: PositionType,
    groupedSymbolsByFundType?: InsightGetSymbolsPerTypeResponse,
): PositionWithExtraInfo[] {
    return positions
        .filter(
            (position) =>
                (!symbol || position.quote.underlyingSymbol === symbol || position.symbol === symbol) &&
                (!positionType || position.type === positionType),
        )
        .map((position) => {
            const account = accounts.find((account) => account.accountId === position.accountId);
            const quote = formatQuote(position.quote);
            const inventoryAccount = inventoryAccounts.find((account) => account.accountId === position.accountId);

            const finalAccount = account || inventoryAccount;
            const accountBalance = account?.balance || 0;

            return {
                ...position,
                ...(hasBreakdownByAccounts(position) && {
                    breakdownByAccounts: formatPortfolioPositions(
                        position.breakdownByAccounts,
                        accounts,
                        inventoryAccounts,
                        totalBalance,
                        symbol,
                        positionType,
                        groupedSymbolsByFundType,
                    ),
                }),
                fullName: finalAccount?.fullName || '',
                accountNumber: finalAccount?.accountNumber || '',
                accountType: finalAccount?.accountType || position.accountType,
                lastPrice: quote.lastPrice,
                weight: totalBalance === 0 ? 0 : divide(Math.abs(position.value), Math.abs(totalBalance)),
                accountWeight: accountBalance === 0 ? 0 : divide(Math.abs(position.value), Math.abs(accountBalance)),
                companyName: quote.companyName,
                marketCapClassification: quote.marketCapClassification || MarketCapClassification.Nano,
                dividendAmount: quote.dividendAmount || 0,
                dividendYield: quote.dividendYield || 0,
                dividendExDate: quote.dividendExDate || '',
                dividendPaymentDate: quote.dividendPaymentDate || '',
                inventory: finalAccount?.accountClassification === AccountClassification.Inventory || false,
                quote: {
                    ...quote,
                    // Mutual fund from quote is not reliable when using `portfolio/full` endpoint
                    mutualFund: groupedSymbolsByFundType?.MF.includes(position.symbol) || quote.mutualFund,
                },
                // Determine security type based on quote data and fund type groupings
                // Priority: Option > Bond > MutualFund > ETF > Equity (default)
                securityType: quote.option
                    ? SecurityType.Option
                    : quote.bond
                      ? SecurityType.Bond
                      : groupedSymbolsByFundType?.MF.includes(position.symbol)
                        ? SecurityType.MutualFund
                        : groupedSymbolsByFundType?.ETF.includes(position.symbol) // Fixed: was incorrectly checking MF instead of ETF
                          ? SecurityType.Etf
                          : SecurityType.Equity,
            };
        });
}

export function formatPortfolio(
    portfolio: Portfolio | undefined,
    accounts: AccountWithExtraInfo[],
    inventoryAccounts: Account[],
    balance: number,
    {
        symbol = undefined,
        positionType = undefined,
        tipranks = undefined,
        groupedSymbolsByFundType = undefined,
    }: {
        symbol?: string;
        positionType?: PositionType;
        tipranks?: TiprankOverviewWithExtraInfo[];
        groupedSymbolsByFundType?: InsightGetSymbolsPerTypeResponse;
    } = {},
): PortfolioWithExtraInfo {
    if (!portfolio) {
        return {
            entries: [],
            totalGainPct: 0,
            containsShort: false,
            homogeneous: true,
            total: 0,
            totalGain: 0,
            containsEquities: false,
            currency: '',
            containsOptions: false,
            totalCost: 0,
            totalDaysValue: 0,
            realizedProfitLoss: { USD: 0 },
            securityTotalValue: 0,
        };
    }

    return {
        ...portfolio,
        entries: formatPortfolioPositions(
            portfolio.entries.map((entry) => {
                const tiprankSymbolData = tipranks?.find(
                    (data) => data.ticker === entry.symbol || data.ticker === entry.quote.underlyingSymbol,
                );
                return {
                    ...entry,
                    tipranks: tiprankSymbolData,
                };
            }),
            accounts,
            inventoryAccounts,
            balance,
            symbol,
            positionType,
            groupedSymbolsByFundType,
        ),
    };
}

/**
 * Calculates the total value of all securities in a portfolio
 *
 * @param portfolio - Array of positions with their values
 * @returns Total portfolio value, handling non-numeric values safely
 *
 * @example
 * const positions = [
 *   { symbol: 'AAPL', value: 10000 },
 *   { symbol: 'GOOGL', value: 5000 }
 * ];
 * getPortfolioSecurityTotalValue(positions); // Returns 15000
 */
export function getPortfolioSecurityTotalValue<T extends Position[]>(portfolio: T): number {
    if (!Array.isArray(portfolio)) {
        return 0;
    }

    return portfolio.reduce((total, item) => {
        // Ensure value is a valid number before adding
        const value = typeof item?.value === 'number' && !isNaN(item.value) ? item.value : 0;
        return total + value;
    }, 0);
}
